import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar } from '@mui/material';
import { Navigate, Outlet, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HelpWidget from '../ui/HelpWidget';
import Layout from './Layout';
import ConfirmSignUp from '../modals/ConfirmSignUp';
import SignIn from '../../views/login/SignIn';
import SignUp from '../../views/login/SignUp';
import { ResetPassword } from '../../views/login/ResetPassword';
import PendingApproval from '../../views/login/PendingApproval';
import PricingPage from '../../views/pricing/PricingPage';
import SubscriptionPage from '../../views/subscription/SubscriptionPage';
import MattersListView from '../../views/matters/MattersListView';
import MatterDetailView from '../../views/matters/MatterDetailView';
import SettingsView from '../../views/settings/SettingsView';
import LandingPage from '../../views/landing/LandingPage';
import TermsOfServicePage from '../../views/legal/TermsOfServicePage';
import PrivacyPolicyPage from '../../views/legal/PrivacyPolicyPage';
import RefundPolicyPage from '../../views/legal/RefundPolicyPage';

interface AuthFormState {
  email: string;
  message: string;
}

const LoadingScreen: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      flexDirection: 'column',
      gap: 2,
      backgroundColor: 'var(--bg)',
      color: 'var(--t1)',
    }}
  >
    <CircularProgress sx={{ color: 'var(--teal)' }} />
    <span>Loading...</span>
  </Box>
);

const AuthenticatedShell: React.FC<{ userName: string; onSignOut: () => Promise<void> }> = ({
  userName,
  onSignOut,
}) => (
  <>
    <Layout userName={userName} onSignOut={onSignOut} />
    <HelpWidget />
  </>
);

const AppContent: React.FC = () => {
  const { user, isAuthenticated, isApproved, isLoading, refreshStatus, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [authFormState, setAuthFormState] = useState<AuthFormState>({ email: '', message: '' });
  const [subscriptionToast, setSubscriptionToast] = useState(false);

  useEffect(() => {
    if (searchParams.get('subscribed') === '1') {
      setSubscriptionToast(true);
      searchParams.delete('subscribed');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const cleanUserName = useMemo(() => {
    if (!user?.name) return 'User';
    if (!user.email) return user.name;
    return user.name.replace(user.email, '').trim() || user.name;
  }, [user?.email, user?.name]);

  const handleSignInSuccess = useCallback(async () => {
    setAuthFormState({ email: '', message: '' });
    const approved = await refreshStatus();
    navigate(approved ? '/' : '/pending-approval', { replace: true });
  }, [navigate, refreshStatus]);

  const handleSignInFailure = useCallback((error: any, email?: string) => {
    setAuthFormState({
      email: email || '',
      message: error?.message || 'Sign in failed. Please try again.',
    });
  }, []);

  const handleSignUpSuccess = useCallback(
    (email: string) => {
      setAuthFormState({
        email,
        message: 'Account created successfully. Please check your email for a verification code.',
      });
      navigate('/confirm-signup');
    },
    [navigate],
  );

  const handleSignUpFailure = useCallback((error: any) => {
    setAuthFormState({
      email: '',
      message: error?.message || String(error) || 'Sign up failed. Please try again.',
    });
  }, []);

  const handleConfirmSignUpSuccess = useCallback(() => {
    setAuthFormState({ email: '', message: 'Account confirmed successfully. Please sign in.' });
    navigate('/login');
  }, [navigate]);

  const handleConfirmSignUpFailure = useCallback(
    (error: any) => {
      setAuthFormState({
        email: authFormState.email,
        message: error?.message || 'Confirmation failed. Please try again.',
      });
    },
    [authFormState.email],
  );

  const handleResetPasswordSuccess = useCallback(() => {
    setAuthFormState({ email: '', message: 'Password reset successfully. Please sign in.' });
    navigate('/login');
  }, [navigate]);

  const handleResetPasswordFailure = useCallback((error: any) => {
    setAuthFormState({
      email: '',
      message: error?.message || 'Password reset failed. Please try again.',
    });
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/', { replace: true });
  }, [navigate, signOut]);

  if (isLoading) return <LoadingScreen />;

  const isReadyForApp = isAuthenticated && isApproved;

  return (
    <>
    <Routes>
      <Route
        path="/login"
        element={
          isReadyForApp ? (
            <Navigate to="/" replace />
          ) : (
            <SignIn
              onSignInSuccess={handleSignInSuccess}
              onSignInFailure={handleSignInFailure}
              onNavigateToSignUp={() => navigate('/signup')}
              onNavigateToResetPassword={() => navigate('/reset-password')}
              onNavigateToConfirmSignUp={() => navigate('/confirm-signup')}
              externalError={authFormState.message}
            />
          )
        }
      />
      <Route
        path="/signup"
        element={
          isReadyForApp ? (
            <Navigate to="/" replace />
          ) : (
            <SignUp
              onSignUpSuccess={handleSignUpSuccess}
              onSignUpFailure={handleSignUpFailure}
              onNavigateToSignIn={() => navigate('/login')}
            />
          )
        }
      />
      <Route
        path="/confirm-signup"
        element={
          <ConfirmSignUp
            email={authFormState.email}
            onConfirmSuccess={handleConfirmSignUpSuccess}
            onConfirmFailure={handleConfirmSignUpFailure}
          />
        }
      />
      <Route
        path="/reset-password"
        element={
          <ResetPassword
            onResetSuccess={handleResetPasswordSuccess}
            onResetFailure={handleResetPasswordFailure}
          />
        }
      />
      <Route
        path="/pending-approval"
        element={
          isReadyForApp ? (
            <Navigate to="/" replace />
          ) : (
            <PricingPage />
          )
        }
      />

      <Route
        element={
          isReadyForApp ? (
            <AuthenticatedShell userName={cleanUserName} onSignOut={handleSignOut} />
          ) : (
            <Outlet />
          )
        }
      >
        <Route
          index
          element={isReadyForApp ? <MattersListView /> : <LandingPage />}
        />
        <Route
          path="/matters"
          element={isReadyForApp ? <MattersListView /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/matters/:matterId"
          element={isReadyForApp ? <MatterDetailView /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/subscription"
          element={isReadyForApp ? <SubscriptionPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/pricing"
          element={isReadyForApp ? <PricingPage /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/settings"
          element={isReadyForApp ? <SettingsView /> : <Navigate to="/login" replace />}
        />
      </Route>

      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/refund" element={<RefundPolicyPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>

    <Snackbar
      open={subscriptionToast}
      autoHideDuration={6000}
      onClose={() => setSubscriptionToast(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSubscriptionToast(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
        Subscription activated successfully! Welcome aboard.
      </Alert>
    </Snackbar>
    </>
  );
};

export default AppContent;
